import { useForm } from 'react-hook-form'

import { logisticApi } from 'redux/services/logistic'
import {
  EditLogisticParams,
  ProcessedLogisticObject,
} from 'redux/services/logistic/types'

import { AppButton } from 'components/AppButton'
import AppCkeckbox from 'components/AppCkeckbox'
import { AppModal } from 'components/AppModal'
import { AppSelect } from 'components/AppSelect'
import { TextField } from 'components/TextField'
import { TextFieldCalendar } from 'components/TextFieldCalendar'
import { TextFieldPhone } from 'components/TextFieldPhone'

import { preparePhoneNumberForCall } from 'shared/libs'

import styles from './Edit.module.scss'
import * as React from 'react'

const COST_OPTIONS = [100, 150, 200]

interface EditProcessedForm extends Omit<EditLogisticParams, 'coordinates'> {
  coordinates: string // ⚡️ строка вместо объекта
}

interface Props {
  data: ProcessedLogisticObject
  onClose: () => void
}
export function EditLogisticModal({ data, onClose }: Props) {
  const {
    watch,
    register,
    clearErrors,
    setValue,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<EditProcessedForm>({
    defaultValues: {
      address: data.address,
      phone: data.phone,
      is_paid: data.is_paid,
      start_delivery: data.start_delivery,
      end_delivery: data.end_delivery,
      cost: data.cost,
      project: data.project?.id,
      coordinates: data.coordinates ? data.coordinates.coordinates.join(', ') : '',
    },
  })
  const { data: projects } = logisticApi.useGetProjectsQuery()
  const [submit, submitState] = logisticApi.useEditLogisticMutation()

  function onSubmit(body: EditProcessedForm) {

    let lon = 0
    let lat = 0

    if (body.coordinates) {
      const parts = body.coordinates.split(',').map(v => Number(v.trim()))
      lon = parts[0] || 0
      lat = parts[1] || 0
    }

    submit({
      id: data.id,
      body: {
        ...body,
        coordinates: {
          type: 'Point',
          coordinates: [lon, lat],
        },
      },
    })
      .unwrap()
      .then(() => {
        onClose()
      })
      .catch(err => {
        try {
          Object.entries(err.data.errors).forEach(([key, messages]) =>
            setError(key as keyof EditLogisticParams, {
              type: 'custom',
              message: Array.isArray(messages) ? messages[0] : messages,
            }),
          )
        } catch (error) {
          console.error(error)
        }
      })
  }

  const startDelivery = data.start_delivery
    ? {
        date: new Date(data.start_delivery),
        hour: new Date(data.start_delivery).getHours(),
        minute: new Date(data.start_delivery).getMinutes(),
      }
    : undefined

  return (
    <AppModal onClose={onClose} title={`Обработка заявки`}>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.container}>
        <TextField
          inputSize="small"
          value={data.client_full_name}
          disabled
          label="ФИО клиента"
        />

        <TextField
          inputSize="small"
          {...register('address', {
            required: 'Обязательное поле',
          })}
          required
          label="Адрес"
          withError
          error={errors.address}
        />

        <TextField
          value={watch('coordinates')}
          {...register('coordinates', {
            required: 'Обязательное поле',
            validate: value => {
              const [lon, lat] = value.split(',').map(v => Number(v.trim()))
              if (isNaN(lon) || isNaN(lat)) {
                return 'Введите координаты в формате: долгота, широта'
              }
              return true
            },
          })}
          required
          label="Долгота и широта (через запятую)"
          withError
          error={errors.coordinates}
        />

        <AppSelect
          label="Проект"
          size="small"
          text={projects?.find(p => p.id === watch('project'))?.name || ''}
          onChangeValues={values => {
            if (values[0]) {
              setValue('project', Number(values[0]))
              clearErrors('project')
            }
          }}
          list={
            projects?.map(item => ({
              value: item.id,
              title: item.name,
            })) || []
          }
          required
          withError
          error={errors.project}
        />

        <AppSelect
          label="Стоимость доставки"
          size="small"
          text={String(watch('cost') || '')}
          onChangeValues={values => {
            if (values[0]) {
              setValue('cost', Number(values[0]))
              clearErrors('cost')
            }
          }}
          list={COST_OPTIONS.map(value => ({ title: String(value), value }))}
          required
          withError
          error={errors.cost}
        />

        <AppCkeckbox
          label="Оплачено"
          checked={watch('is_paid')}
          onChange={v => setValue('is_paid', v)}
        />

        <div style={{ display: 'flex', gap: '1rem' }}>
          <div style={{ flex: 1 }}>
            <TextFieldCalendar
              required
              onChangeDate={date => {
                setValue(
                  'start_delivery',
                  `${date.date} ${date.hour}:${date.minute}`,
                )
                clearErrors('start_delivery')
              }}
              defaultValues={startDelivery}
              label="Время от"
              withError
              error={errors.start_delivery}
            />
          </div>

          <div style={{ flex: 1 }}>
            <TextField
              {...register('end_delivery')}
              type="time"
              required
              label="до"
              withError
              error={errors.end_delivery}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1rem' }}>
          <div style={{ flex: 1 }}>
            <TextFieldPhone
              inputSize="small"
              onChangePhone={phone => {
                setValue('phone', phone.formattedPhone)
                clearErrors('phone')
              }}
              required
              value={watch('phone')}
              withError
              error={errors.phone}
            />
          </div>
          {watch('phone') && (
            <div style={{ flex: 1, marginTop: 25 }}>
              <a href={`tel:${preparePhoneNumberForCall(watch('phone'))}`}>
                <AppButton size="small" type="button">
                  Позвонить
                </AppButton>
              </a>
            </div>
          )}
        </div>

        <TextField
          label="Тип"
          inputSize="small"
          value={data.card_type}
          disabled
        />
        <TextField
          label="ID Карты"
          inputSize="small"
          value={data.barcode}
          disabled
        />

        <AppButton disabled={submitState.isLoading} type="submit">
          Сохранить
        </AppButton>
      </form>
    </AppModal>
  )
}
